import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageSpinner } from "@/components/Spinner";
import { toast } from "sonner";

/**
 * Handles the OAuth callback from Emergent Google Auth. Reads session_id from
 * the URL fragment, exchanges it for our JWT cookie via the backend, then
 * redirects to the dashboard. Runs BEFORE ProtectedRoute so the AuthContext
 * `checkSession` race is avoided (see AuthContext hash-skip).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  // useRef (not useState) so StrictMode's double-invoke can't double-process.
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    if (!sessionId) {
      nav("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        await api.post(
          "/auth/google/session",
          {},
          { headers: { "X-Session-ID": sessionId } }
        );
        // Clear the fragment so a refresh doesn't retry.
        window.history.replaceState(null, "", window.location.pathname);
        await refresh();
        toast.success("Signed in with Google");
        nav("/", { replace: true });
      } catch (e) {
        toast.error(formatApiError(e) || "Google sign-in failed");
        nav("/login", { replace: true });
      }
    })();
     
  }, []);

  return <PageSpinner />;
}
